import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';

import { POPUP_DEFAULT_OPTIONS } from './constants';
import { clsx } from 'clsx';
import * as styles from './styles.css';
import { getContainerPopupPlacement, EPopupPlacement } from './utils';
import { NewbuildingPromoStubPopupMarkup, NewbuildingPromoStubPopup } from '../../../components/NewbuildingPromoPopup';
import { NewbuildingPromoPopupLoadable } from '../../../containers/NewbuildingPromoPopupContainer';

export function defineNewbuildingPromoPopupManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('NewbuildingPromoPopupManager')) {
    return;
  }

  ymaps.modules.define<[typeof YMaps.Balloon, YMaps.ITemplateLayoutFactory]>(
    'NewbuildingPromoPopupManager',
    ['Balloon', 'templateLayoutFactory'],
    (provide, Balloon: typeof YMaps.Balloon, templateLayoutFactory) => {
      class NewbuildingPromoPopupManager {
        private static templateContainerId = 'newbuilding-promo-popup-id';

        private map: YMaps.Map;
        private balloon: YMaps.Balloon;
        private isRedesign: boolean;
        private popupRoot: Root | null = null;
        private popupRootContainer: HTMLElement | null = null;

        public constructor({ map, isRedesign }: YMaps.INewbuildingPromoPopupManagerParameters) {
          this.isRedesign = isRedesign;
          this.map = map;
          this.balloon = new Balloon(this.map, {
            ...POPUP_DEFAULT_OPTIONS,
            layout: this.preparePopupLayout(),
          });
          this.balloon.options.setParent(this.map.options);
        }

        public getBalloon = () => {
          return this.balloon;
        };

        public setIndent = (indent: YMaps.TPopupIndent) => {
          this.balloon.options.set<YMaps.TPopupIndent>('indent', indent);
        };

        public setSideIndent = (sideIndent?: number) => {
          if (this.isRedesign) {
            this.balloon.options.set<number | undefined>('sideIndent', sideIndent);
          }
        };

        public setState = async (state: YMaps.TNewbuildingPromoPopupManagerState) => {
          if (state.status === 'error') {
            this.balloon.close();

            return;
          }
          this.balloon.options.set<YMaps.TNewbuildingPromoPopupManagerState>(state);

          const container = document.getElementById(NewbuildingPromoPopupManager.templateContainerId);

          if (state.status === 'pending' && container) {
            this.getPopupRoot(container).render(React.createElement(NewbuildingPromoStubPopup));
          }

          if (state.status === 'completed' && container) {
            this.getPopupRoot(container).render(
              React.createElement(NewbuildingPromoPopupLoadable, {
                payload: { ...state, isRedesign: this.isRedesign },
              }),
            );
          }
        };

        /**
         * Балун пересоздаёт разметку, поэтому root переиспользуется пока контейнер тот же:
         * повторный createRoot на одном контейнере в React 19 приводит к предупреждению и утечке.
         */
        private getPopupRoot = (container: HTMLElement): Root => {
          if (this.popupRootContainer !== container || !this.popupRoot) {
            this.popupRoot?.unmount();
            this.popupRoot = createRoot(container);
            this.popupRootContainer = container;
          }

          return this.popupRoot;
        };

        public open = (coordinates: YMaps.TCoord) => {
          const status = this.balloon.options.get('status');

          if (status === 'error') {
            this.balloon.close();

            return;
          }

          const mapBounds = this.map.getBounds({ useMapMargin: true });
          const zoom = this.map.getZoom();
          const preferredPlacement = this.isRedesign ? EPopupPlacement.Bottom : undefined;
          const containerPopupPlacement = getContainerPopupPlacement(coordinates, mapBounds, zoom, preferredPlacement);

          this.balloon.options.set<EPopupPlacement>('placement', containerPopupPlacement);
          this.balloon.open(coordinates);
        };

        public close = () => {
          this.balloon.close();
        };

        private preparePopupLayout = () => {
          const content = this.preparePopupContent();

          return templateLayoutFactory.createClass(content);
        };

        private preparePopupContent() {
          return `
            <div
              {% if options.sideIndent %} style="--popup-side-indent: {{ options.sideIndent }}px"{% endif %}
              class="${styles['wrapper']}
              {% if options.indent === 'S' %} ${styles['indent--small']}
              {% elseif options.indent === 'M' %} ${styles['indent--medium']}
              {% elseif options.indent === 'L' %} ${styles['indent--large']}
              {% elseif options.indent === 'XL' %} ${styles['indent--extra-large']}{% endif %}
              {% if options.placement === '${EPopupPlacement.Left}' %} ${styles['placement--left']}
              {% elseif options.placement === '${EPopupPlacement.Right}' %} ${styles['placement--right']}
              {% elseif options.placement === '${EPopupPlacement.Top}' %} ${styles['placement--top']}
              {% elseif options.placement === '${EPopupPlacement.Bottom}' %} ${styles['placement--bottom']}
              {% elseif options.placement === '${EPopupPlacement.TopRight}' %} ${styles['placement--top-right']}
              {% elseif options.placement === '${EPopupPlacement.TopLeft}' %} ${styles['placement--top-left']}
              {% elseif options.placement === '${EPopupPlacement.BottomRight}' %} ${styles['placement--bottom-right']}
              {% elseif options.placement === '${EPopupPlacement.BottomLeft}' %} ${styles['placement--bottom-left']}{% endif %}
              {% if options.sideIndent %} ${styles['side-indent']}{% endif %}
            ">
              <div class="${clsx(styles['container'], this.isRedesign && styles['redesign'])}">
                <div id="${NewbuildingPromoPopupManager.templateContainerId}">
                    ${NewbuildingPromoStubPopupMarkup}
                </div>
              </div>
            </div>
          `;
        }
      }

      provide(NewbuildingPromoPopupManager);
    },
  );
}
